import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { MouseEvent, ReactNode } from 'react';
import { styles } from '../../styles';
import { entitiesWithSimilarFields, ownerLabelText } from '../../constants';
import { OwnerType, TOwnerLink, TOwnerNode } from '../../types';
import { DragIndicator, ExpandMore, Error, Link } from '@mui/icons-material';
import { getOwnerName, getOwnerTypeIcon } from '../../utils/utils.tsx';
import Pencil from '~/components/icons/Pencil';
import Delete from '~/assets/icons/common/Delete.svg?react';

interface IOwnerCardViewProps {
  owner: TOwnerNode | null;
  isExpandable?: boolean;
  expanded?: boolean;
  handleRef?: (element: Element | null) => void;
  handleEdit?: (e: MouseEvent<HTMLButtonElement>) => void;
  handleDelete?: (e: MouseEvent<HTMLButtonElement>) => void;
  handleExpandClick?: () => void;
  showError?: boolean;
  childError?: boolean;
  currentLink?: TOwnerLink;
  children?: ReactNode;
  parents?: string[];
}

export const OwnerCardView = ({
  owner,
  isExpandable,
  expanded,
  handleRef,
  handleEdit,
  handleExpandClick,
  handleDelete,
  showError,
  childError,
  currentLink,
  children,
  parents,
}: IOwnerCardViewProps) => {
  const { type, identificationNumber, level, isBeneficiary } = owner || {};
  const isIndividual = owner?.type === OwnerType.INDIVIDUAL;
  const showIdentNumber = entitiesWithSimilarFields.includes(type as OwnerType);
  const namePlaceholder = isIndividual ? 'ПІБ' : 'Найменування';
  const identCodePlacrholder = isIndividual ? 'РНОКПП' : 'ЄДРПОУ';
  const name = getOwnerName(owner);
  const uniqueParents = new Set(parents);
  const showLinkTooltip = uniqueParents && uniqueParents.size > 1;
  const showOwnershipPercentage =
    !isNaN(Number(currentLink?.ownershipPercentage)) && currentLink?.ownershipPercentage !== null;

  return (
    <Box sx={styles.card}>
      <Box sx={styles.cardInfoContainer} onClick={() => handleExpandClick?.()}>
        {level && <Chip label={`Рівень ${level}`} variant="outlined" sx={styles.chip} />}

        {isExpandable && (
          <ExpandMore
            onClick={handleExpandClick}
            sx={{ transform: `rotate(${expanded ? 0 : -90}deg)`, cursor: 'pointer', flexShrink: 0 }}
            color={childError ? 'error' : 'action'}
            data-testid={'extande-button'}
          />
        )}
        {level && (
          <Box
            component="span"
            ref={handleRef}
            onClick={(e) => e.stopPropagation()}
            sx={styles.dragIcon}
          >
            <DragIndicator fontSize="small" data-testid={'move-six-dots'} />
          </Box>
        )}
        <Box sx={styles.nameContainer}>
          {getOwnerTypeIcon(type)}
          <Box sx={styles.nameTextContainer}>
            {name.trim() ? (
              <Typography sx={styles.wrapText}>{name}</Typography>
            ) : (
              <Typography sx={{ ...styles.wrapText, color: 'grey' }}>{namePlaceholder}</Typography>
            )}
            {showIdentNumber && (
              <Typography sx={{ ...styles.wrapText, color: 'grey' }}>
                {identCodePlacrholder}
                {!!identificationNumber && ': ' + identificationNumber}
              </Typography>
            )}
          </Box>
          {showError && (
            <Tooltip title={'Перевірте коректність заповнення даних'} placement="top">
              <Error color="error" data-testid={'error-informer'} />
            </Tooltip>
          )}
        </Box>
        <Box sx={styles.chipContainer}>
          {type && (
            <Chip label={ownerLabelText[type as OwnerType]} variant="outlined" sx={styles.chip} />
          )}
          {isBeneficiary && <Chip label={`КБВ`} variant="outlined" sx={styles.chip} />}
          {showOwnershipPercentage && (
            <Chip
              label={`${currentLink?.ownershipPercentage}%`}
              variant="outlined"
              sx={styles.chip}
            />
          )}
        </Box>
        {showLinkTooltip && (
          <Tooltip
            title={
              <>
                <Typography>Цей учасник також присутній у:</Typography>
                {Array.from(uniqueParents).map((item) => (
                  <Typography key={item}>• {item}</Typography>
                ))}
              </>
            }
          >
            <Link color="primary" data-testid={'link'} />
          </Tooltip>
        )}

        {level && (
          <Box sx={styles.cardActionsContainer}>
            <IconButton onClick={(e) => handleEdit?.(e)}>
              <Pencil testId={'edit'} />
            </IconButton>
            <IconButton
              sx={{ width: 35, height: 35 }}
              onClick={(e) => handleDelete?.(e)}
              data-testid={'delete'}
            >
              <Delete />
            </IconButton>
          </Box>
        )}
      </Box>
      {children}
    </Box>
  );
};
